import {TeamStatsIcon as BarChart3,VodReviewIcon as Video} from '../icons/AppIcons';
import React,{lazy,Suspense,useEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {useNavigate} from 'react-router-dom';
import {X} from 'lucide-react';
import {collection,doc,getDoc,getDocs,query,where} from 'firebase/firestore';
import {db} from '../../Security/firebase-config';
import {eventSlots,hasSetupEventId,setupKey} from '../../utils/eventSetup';
import {agendaEventTitle} from '../Calendar/dayAgenda';
import {eventStatsScore} from './eventStatsScore';
import useEventResourcePresence, {isAddedOverviewVod} from './useEventResourcePresence';

import './OverviewResourceChooser.css';
import {useEventWorkspace} from '../event-workspace/EventWorkspaceContext';

const UploadVideoModal=lazy(()=>import('../Review/UploadVideoModal'));
const LiveRecordModal=lazy(()=>import('../Review/LiveRecordModal'));
const UploadScoreDialog=lazy(()=>import('../../core-pages/team-stats/UploadScoreDialog'));

function ResourceChooser({title,onClose,children}) {
  const dialog=useRef(null);
  useEffect(()=>{
    const previous=document.activeElement,overflow=document.body.style.overflow,element=dialog.current;
    document.body.style.overflow='hidden';element.showModal();element.querySelector('button')?.focus();
    return()=>{element.close();document.body.style.overflow=overflow;if(previous?.isConnected)previous.focus();};
  },[]);
  return createPortal(<dialog className="overview-resource-chooser" ref={dialog} aria-label={title} onCancel={e=>{e.preventDefault();onClose();}}>
    <header><h3>{title}</h3><button type="button" aria-label="Close event resources" onClick={onClose}><X size={18}/></button></header>{children}
  </dialog>,document.body);
}

export function OverviewResourceUpload({selection,onClose}) {
  const {kind,event,slot}=selection;
  const key=setupKey(event,slot.id),teamId=event.__teamId;
  return createPortal(<Suspense fallback={<ResourceChooser title="Loading upload menu" onClose={onClose}><p role="status">Loading…</p></ResourceChooser>}>
    {kind==='record'?<LiveRecordModal mapId={slot.mapId||''} mapName={slot.mapName||''} initialSetupEventId={key} onClose={onClose} onVideoUploaded={onClose}/>:kind==='vod'?<UploadVideoModal teamId={teamId} mapId={slot.mapId||''} mapName={slot.mapName||''} initialSetupEventId={key} onClose={onClose} onVideoUploaded={onClose}/>
      :<UploadScoreDialog teamId={teamId} initialSetupEventId={key} onClose={onClose} onSaved={onClose}/>}
  </Suspense>,document.body);
}

export default function OverviewEventResources({event,onAdd,viewerId,viewerName,timeZone}) {
  const navigate=useNavigate(),workspace=useEventWorkspace();
  const [chooser,setChooser]=useState(null),[busy,setBusy]=useState(false),[error,setError]=useState('');
  const mounted=useRef(true),request=useRef(0);
  useEffect(()=>{mounted.current=true;request.current++;return()=>{mounted.current=false;};},[]);
  const desktop=!!window.desktopAPI,title=agendaEventTitle(event),teamId=event.__teamId;
  const slots=eventSlots(event);
  const presence=useEventResourcePresence(event,desktop,viewerId,viewerName);
  const score=eventStatsScore(presence.stats.scores||[]);
  const resourceTitle=(kind)=>{
    const item=presence[kind],label=kind==='vod'?'VODs':'Team Stats';
    const status=item.error?'Status unavailable':item.checking?'Checking status…':kind==='vod'
      ?item.reviewed?'No unread notes relevant to you':item.unreadCount>0?`${item.unreadCount} unread note${item.unreadCount===1?'':'s'} relevant to you`:'Recording is still uploading or processing'
      :item.reviewed?'All match statistics completed':'Match statistics incomplete';
    return item.present?`${label} · ${status} · Open ${label}`:item.checking?`Checking ${label}…`:item.error?`${label} status unavailable · Click to check`:`No ${label} added yet · ${kind==='vod'?'Upload a VOD':'Add Team Stats'}`;
  };
  const close=()=>{request.current++;setChooser(null);setBusy(false);setError('');};
  const add=(kind,slot)=>{setChooser(null);onAdd({kind,event,slot});};
  const videoRoute=(slot,id,local=false)=>local?`/review?teamId=${encodeURIComponent(teamId)}&map=${encodeURIComponent(slot.mapId||'')}&localMatch=${encodeURIComponent(id)}`
    :desktop?`/review?teamId=${encodeURIComponent(teamId)}&map=${encodeURIComponent(slot.mapId)}&video=${encodeURIComponent(id)}&play=1`
      :`/review/${encodeURIComponent(slot.mapId)}/${encodeURIComponent(id)}?teamId=${encodeURIComponent(teamId)}`;
  async function openSlot(kind,slot) {
    const token=++request.current;
    const active=()=>mounted.current&&token===request.current;
    setBusy(true);setError('');
    try {
      if(kind==='stats') {
        if(!slot.statsId){add(kind,slot);return;}
        const snapshot=await getDoc(doc(db,'teams',teamId,'matches',slot.statsId));
        if(!active())return;
        const data=snapshot.data?.();
        if(!snapshot.exists()||data?.deleted||data?.isDeleted||data?.deletedAt){add(kind,slot);return;}
        setChooser(null);navigate(`/team-stats?teamId=${encodeURIComponent(teamId)}&match=${encodeURIComponent(slot.statsId)}`);
      } else {
        const videos=[];
        if(slot.matchId&&slot.mapId) {
          const snapshot=await getDocs(query(collection(db,'teams',teamId,'maps',slot.mapId,'videos'),where('matchId','==',slot.matchId)));
          if(!active())return;
          for(const item of snapshot.docs){
            const data=item.data()||{},status=String(data.status||data.processingStatus||'').toLowerCase();
            const pending=['uploading','processing','queued'].includes(status)&&!data.deleted&&!data.isDeleted&&!data.deletedAt;
            if(isAddedOverviewVod(data))videos.push({id:item.id,label:(data.povName||data.title||data.fileName||'VOD')+(pending?` (${status})`:''),local:false});
          }
        }
        if(desktop&&slot.localMatchId)videos.push({id:slot.localMatchId,label:'Local recording',local:true});
        if(!videos.length){add(kind,slot);return;}
        if(videos.length===1){setChooser(null);navigate(videoRoute(slot,videos[0].id,videos[0].local));}
        else setChooser({kind,slot,videos});
      }
    }catch(failure){if(active())setError(failure.message||'Could not load event resources.');}
    finally{if(active())setBusy(false);}
  }
  function open(kind) {
    if(workspace){workspace({...event,__workspaceZone:timeZone},kind==='vod'?'vod':'stats');return;}
    setError('');
    if(slots.length>1)setChooser({kind});
    else void openSlot(kind,slots[0]);
  }
  return <>
    <div className="overview-week__resources" onClick={e=>e.stopPropagation()}>
      {score&&<span className={`overview-event-score is-${score.result}`} title={score.title} aria-label={score.title}>{score.text}</span>}
      <button type="button" className={`overview-icon-button overview-resource--vod ${presence.vod.present?'has-content ':''}${presence.vod.status}`} aria-label={`VODs for ${title}`} aria-busy={presence.vod.checking} title={resourceTitle('vod')} disabled={busy||!hasSetupEventId(event)} onClick={()=>open('vod')}><Video size={15}/>{presence.vod.unreadCount>0&&<span className="overview-resource__unread" aria-label={`${presence.vod.unreadCount} relevant notes not reviewed`}>{presence.vod.unreadCount}</span>}</button>
      <button type="button" className={`overview-icon-button overview-resource--stats ${presence.stats.present?'has-content ':''}${presence.stats.status}`} aria-label={`Team Stats for ${title}`} aria-busy={presence.stats.checking} title={resourceTitle('stats')} disabled={busy||!hasSetupEventId(event)} onClick={()=>open('stats')}><BarChart3 size={15}/></button>
    </div>
    {busy&&!chooser&&<small role="status">Loading linked content…</small>}
    {error&&!chooser&&<p role="alert">{error}</p>}
    {chooser&&<ResourceChooser title={chooser.videos?'Choose a VOD POV':chooser.kind==='vod'?'Event VODs':'Event Team Stats'} onClose={close}>
      <p>{title}</p>
      <div className="overview-resource-chooser__choices">{chooser.videos?chooser.videos.map(item=><button type="button" key={`${item.local}:${item.id}`} onClick={()=>{navigate(videoRoute(chooser.slot,item.id,item.local));close();}}>{item.label}</button>)
        :slots.map((slot,index)=>{
          const item=presence.bySlot[slot.id][chooser.kind],vod=chooser.kind==='vod';
          const mapScore=vod?null:eventStatsScore(item.scores||[]);
          const action=item.present?(vod?'View VODs':'View Team Stats'):item.checking||item.error?(vod?'Check VODs':'Check Team Stats'):(vod?'Upload VOD':'Add Team Stats');
          return <button key={slot.id} className="overview-resource-map" data-slot-id={slot.id} type="button" disabled={busy} onClick={()=>openSlot(chooser.kind,slot)}>
            <span className="overview-resource-map__label"><strong>Map {index+1} · {slot.mapName||'Map TBD'}</strong><small>{action}</small></span>
            <span className={`overview-resource-map__summary${vod&&item.unreadCount>0?' has-unread':''}`} aria-live="polite">
              {item.error?'Status unavailable':item.checking?'Checking…':vod?item.present?`${item.unreadCount} unread note${item.unreadCount===1?'':'s'}`:'No VODs added':mapScore?<><strong className={`overview-event-score is-${mapScore.result}`} aria-label={mapScore.title}>{mapScore.text}</strong><small>{mapScore.result==='win'?'Win':mapScore.result==='loss'?'Loss':'Draw'}</small></>:item.present?'Score not recorded':'No Team Stats added'}
            </span>
          </button>;
        })}</div>
      {busy&&<p role="status">Loading linked content…</p>}{error&&<p role="alert">{error}</p>}
    </ResourceChooser>}
  </>;
}
